import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { formInitialize, formSet, formUninitialize, useCpFormSubmit } from "../gvc/testNewForm/utils";
import { useAppDispatch } from "../../store/hooks";
import { formActions } from "../../store/formSlice";
import { Modal, notification } from "antd";
import { linksOf, request } from "../../services/cpln";
import { CpFormPromptDetail } from "../../components/antd/form/CpFormPromptDetail";
import { CpFormContext } from "../../components/form/CpFormContext";
import { CpFormValidator } from "../gvc/testNewForm/CpFormValidator";
import { CpPreviewModal } from "../workload/draft/previewModal";
import { CpDebugModal } from "../workload/draft/debugModal";
import { CloudAccount } from "../../schema/types/cloudaccount/entity";
import { useGetCloudAccountQuery } from "../../store/api/cloudaccountApi";
import { cloudAccountToInput, cloudAccountToPatch, inputToCloudAccountPartial } from "./utils";
import { CloudAccountInput } from "../../schema/types/cloudaccount/input";
import { getValidator } from "./create/validator";
import { CloudAccountDetailContent } from "./detailContent";
import { getDirtyChecker } from "./create/dirtyChecker";
import { CpFormDirtyChecker } from "../gvc/testNewForm/CpFormDirtyChecker";

interface Props {
  cloudAccount: CloudAccount;
}
export const CloudAccountDetailForm: React.FC<Props> = ({ cloudAccount }) => {
  const dispatch = useAppDispatch();
  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  const { refetch } = useGetCloudAccountQuery(linksOf(cloudAccount).self!);

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = cloudAccountToInput(cloudAccount);
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // confirm
  React.useEffect(() => {
    dispatch(formActions.setInitialValues({ id: formId, values: cloudAccountToInput(cloudAccount) }));
  }, [cloudAccount.version]);

  // validation/dirty/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const dirtyCheckerRef = React.useRef(getDirtyChecker(formId));
  const dirtyChecker = dirtyCheckerRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: CloudAccountInput) {
    try {
      let body = cloudAccountToPatch(inputToCloudAccountPartial(input), { id: input.id, version: input.version });
      body.data = cloudAccount.data as any;
      const method = "patch";
      const url = linksOf(cloudAccount).self!;

      await request({
        method: method,
        url: url,
        body: body,
      });

      formSet(formId, `meta.applied`, true);

      notification.success({
        message: "Success",
        description: "Cloud Account is updated",
      });

      refetch();
    } catch (e) {
      if (e?.response?.status === 409) {
        Modal.warning({
          title: "Update Conflict",
          content: "This resource has been modified since you opened it. The form will be refreshed with the latest data and your changes will be lost.",
          onOk: () => {
            refetch();
          },
        });
        return;
      }

      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <CloudAccountDetailContent cloudAccount={cloudAccount} refetch={refetch} />
      <CpFormValidator validationFn={validator} />
      <CpFormDirtyChecker checker={dirtyChecker} />
      {/* TODO this is not complete because of secret creation */}
      <CpFormPromptDetail link={linksOf(cloudAccount).self!} inputToItemFn={inputToCloudAccountPartial} />
      <CpPreviewModal
        inputToItemFn={inputToCloudAccountPartial}
        kind="cloudAccount"
        namePath="name"
        title="Cloud Account"
      />
      <CpDebugModal
        inputToItemFn={inputToCloudAccountPartial}
        kind="cloudAccount"
        namePath="name"
        title="Cloud Account"
      />
    </CpFormContext.Provider>
  );
};
